import { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import {
  CheckCircle2, AlertTriangle, Wrench, Users, Truck, Leaf,
  TrendingUp, RefreshCw, ShieldCheck, Activity, BarChart2, Package
} from 'lucide-react';
import api from '../api/api';

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const WO_STATUS_COLORS = { pending: '#f59e0b', in_progress: '#3b82f6', completed: '#16a34a' };
const WO_STATUS_LABELS = { pending: 'Pendiente', in_progress: 'En Curso', completed: 'Completada' };
const DEPT_COLORS = ['#16a34a', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#10b981', '#6b7280'];

const INC_SEV = {
  critical: { cls: 'border-red-500 bg-red-50', badge: 'bg-red-100 text-red-700', l: 'Crítica' },
  high: { cls: 'border-orange-400 bg-orange-50', badge: 'bg-orange-100 text-orange-700', l: 'Alta' },
  medium: { cls: 'border-yellow-400 bg-yellow-50', badge: 'bg-yellow-100 text-yellow-800', l: 'Media' },
};

export default function CuadroMandos() {
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState(null);
  const [ops, setOps] = useState(null);
  const [personal, setPersonal] = useState(null);
  const [env, setEnv] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [access, setAccess] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [kpiR, opsR, perR, envR, incR, accR, woR] = await Promise.all([
        api.get('/reports/kpi'),
        api.get('/operations/summary'),
        api.get('/personal/summary'),
        api.get(`/environmental?year=${new Date().getFullYear()}`),
        api.get('/incidents'),
        api.get('/security/access?limit=50'),
        api.get('/work-orders'),
      ]);
      setKpi(kpiR.data);
      setOps(opsR.data);
      setPersonal(perR.data);
      setEnv(envR.data);
      setIncidents(incR.data);
      setAccess(accR.data);
      setWorkOrders(woR.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const accessToday = access.filter(a => a.entry_time?.startsWith(today)).length;
  const inPlant = access.filter(a => !a.exit_time).length;
  const latestEnv = env.length > 0 ? [...env].sort((a, b) => b.year !== a.year ? b.year - a.year : b.month - a.month)[0] : null;
  const openIncidents = incidents.filter(i => i.status === 'open' || i.status === 'in_progress');

  // WO status pie
  const woStatusMap = workOrders.reduce((acc, wo) => {
    acc[wo.status] = (acc[wo.status] || 0) + 1;
    return acc;
  }, {});
  const woPieData = Object.entries(woStatusMap).map(([k, v]) => ({ name: WO_STATUS_LABELS[k] || k, value: v, key: k }));

  // Production monthly for chart
  const prodMonthly = ops?.monthly_data ? [...ops.monthly_data].sort((a, b) => a.month - b.month) : [];

  // Personnel by dept
  const deptData = personal?.by_department || [];

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#16a34a]" />
    </div>
  );

  return (
    <div className="p-6 md:p-8 page-enter space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Cuadro de Mandos Integral</h1>
          <p className="text-sm text-gray-500 mt-0.5">Vista ejecutiva · Ecoparque de Cáceres · {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long' })}</p>
        </div>
        <button onClick={loadAll} className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 text-sm" data-testid="refresh-dashboard-btn">
          <RefreshCw size={15} /> Actualizar
        </button>
      </div>

      {/* Global KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { icon: CheckCircle2, label: 'Equipos Operativos', value: `${kpi?.operative}/${kpi?.total_equipment}`, sub: `${kpi?.breakdown} avería`, color: '#16a34a', testid: 'kpi-equipos' },
          { icon: AlertTriangle, label: 'Alertas Activas', value: kpi?.alert_count, sub: 'Requieren atención', color: '#f59e0b', testid: 'kpi-alertas' },
          { icon: Wrench, label: 'OTs Pendientes', value: kpi?.pending_wo, sub: `${kpi?.completed_wo} completadas`, color: '#3b82f6', testid: 'kpi-ots' },
          { icon: TrendingUp, label: 'Entrada Residuos', value: ops ? `${(ops.total_input_tons / (ops.months_recorded || 1)).toFixed(0)} t/mes` : '—', sub: `Tasa val. ${ops?.avg_recovery_rate?.toFixed(1)}%`, color: '#10b981', testid: 'kpi-produccion' },
          { icon: Users, label: 'Personal Activo', value: personal?.active_employees ?? '—', sub: `${personal?.total_employees ?? '—'} total`, color: '#8b5cf6', testid: 'kpi-personal' },
          { icon: Activity, label: 'Incidencias Abiertas', value: openIncidents.length, sub: `${incidents.length} total`, color: openIncidents.length > 0 ? '#ef4444' : '#16a34a', testid: 'kpi-incidencias' },
        ].map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col gap-2" data-testid={s.testid}>
              <div className="flex items-center justify-between">
                <div className="p-1.5 rounded-lg" style={{ backgroundColor: s.color + '15' }}>
                  <Icon size={16} style={{ color: s.color }} />
                </div>
              </div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Barlow, sans-serif', color: s.color }}>{s.value}</p>
              <div>
                <p className="text-xs font-medium text-gray-700 leading-tight">{s.label}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{s.sub}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Row 2: Production Chart + OT Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Production monthly */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart2 size={16} className="text-[#16a34a]" />
            <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Producción Mensual — Entrada de Residuos {new Date().getFullYear()}</h3>
          </div>
          {prodMonthly.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Sin datos de producción</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={prodMonthly} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tickFormatter={m => MONTHS[m - 1]} tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <Tooltip formatter={(v, n) => [`${v} t`, n === 'total_input_tons' ? 'Entrada' : n === 'rejected_tons' ? 'Rechazo' : 'Valorizado']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="total_input_tons" name="Entrada" fill="#16a34a" radius={[4, 4, 0, 0]} />
                <Bar dataKey="rejected_tons" name="Rechazo" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
          {ops && (
            <div className="flex gap-4 mt-3 pt-3 border-t border-gray-100">
              {[
                { l: 'Tasa Valorización', v: `${ops.avg_recovery_rate?.toFixed(1)}%`, c: '#16a34a' },
                { l: 'Tasa Rechazo', v: `${ops.avg_rejection_rate?.toFixed(1)}%`, c: '#ef4444' },
                { l: 'Tickets báscula', v: ops.total_weighing_tickets, c: '#6b7280' },
              ].map(s => (
                <div key={s.l} className="text-center flex-1">
                  <p className="text-xs text-gray-500">{s.l}</p>
                  <p className="text-base font-bold" style={{ color: s.c }}>{s.v}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* OT Status Pie */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Wrench size={16} className="text-[#3b82f6]" />
            <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Estado Órdenes de Trabajo</h3>
          </div>
          {woPieData.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Sin órdenes</div>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={woPieData} cx="50%" cy="50%" outerRadius={65} dataKey="value" label={({ name, value }) => `${value}`} labelLine={false} fontSize={11}>
                  {woPieData.map((entry) => <Cell key={entry.key} fill={WO_STATUS_COLORS[entry.key] || '#94a3b8'} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="space-y-2 mt-2">
            {woPieData.map(entry => (
              <div key={entry.key} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: WO_STATUS_COLORS[entry.key] || '#94a3b8' }} />
                  <span className="text-gray-600">{entry.name}</span>
                </div>
                <span className="font-bold text-gray-900">{entry.value}</span>
              </div>
            ))}
          </div>
          {kpi && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center">Horas flota este año: <span className="font-bold text-gray-900">{kpi.hours_this_year?.toLocaleString('es-ES')} h</span></p>
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Environmental + Personnel + Access */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* Environmental */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="env-panel">
          <div className="flex items-center gap-2 mb-4">
            <Leaf size={16} className="text-emerald-500" />
            <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Control Ambiental</h3>
            {latestEnv && <span className="text-[10px] text-gray-400 ml-auto">{MONTHS[latestEnv.month - 1]} {latestEnv.year}</span>}
          </div>
          {!latestEnv ? (
            <p className="text-sm text-gray-400 text-center py-6">Sin datos ambientales</p>
          ) : (
            <div className="space-y-3">
              {[
                { l: 'Lixiviados', v: `${latestEnv.leachate_volume_m3} m³`, sub: `pH ${latestEnv.leachate_ph ?? '—'}`, color: '#3b82f6' },
                { l: 'Energía', v: `${latestEnv.energy_kwh?.toLocaleString('es-ES')} kWh`, sub: '', color: '#8b5cf6' },
                { l: 'Combustible', v: `${latestEnv.fuel_liters?.toLocaleString('es-ES')} L`, sub: '', color: '#f59e0b' },
                { l: 'Ruido máx.', v: `${latestEnv.noise_db_max ?? '—'} dB`, sub: `Avg ${latestEnv.noise_db_avg ?? '—'} dB`, color: latestEnv.noise_db_max > 70 ? '#ef4444' : '#16a34a' },
              ].map(s => (
                <div key={s.l} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-xs font-medium text-gray-700">{s.l}</p>
                    {s.sub && <p className="text-[10px] text-gray-400">{s.sub}</p>}
                  </div>
                  <p className="text-sm font-bold" style={{ color: s.color }}>{s.v}</p>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-1">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${latestEnv.pest_control_done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {latestEnv.pest_control_done ? 'Control plagas OK' : 'Plagas pendiente'}
                </span>
                {latestEnv.odour_incidents > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">{latestEnv.odour_incidents} inc. olores</span>}
              </div>
            </div>
          )}
        </div>

        {/* Personnel by dept */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="personal-panel">
          <div className="flex items-center gap-2 mb-4">
            <Users size={16} className="text-[#8b5cf6]" />
            <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Personal por Departamento</h3>
          </div>
          {deptData.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">Sin datos</p>
          ) : (
            <div className="space-y-2">
              {deptData.sort((a, b) => b.count - a.count).map((d, i) => {
                const maxCount = Math.max(...deptData.map(x => x.count), 1);
                return (
                  <div key={d.dept}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-700 truncate max-w-[160px]">{d.dept}</span>
                      <span className="font-bold text-gray-900 ml-2">{d.count}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full">
                      <div className="h-full rounded-full transition-all" style={{ width: `${(d.count / maxCount) * 100}%`, backgroundColor: DEPT_COLORS[i % DEPT_COLORS.length] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {personal && (
            <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-2 gap-3">
              <div className="text-center">
                <p className="text-xl font-bold text-[#16a34a]" style={{ fontFamily: 'Barlow, sans-serif' }}>{personal.active_employees}</p>
                <p className="text-[10px] text-gray-500">Activos</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-[#f59e0b]" style={{ fontFamily: 'Barlow, sans-serif' }}>{personal.absent_this_month + personal.sick_this_month}</p>
                <p className="text-[10px] text-gray-500">Ausentes mes</p>
              </div>
            </div>
          )}
        </div>

        {/* Access + Security */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="access-panel">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={16} className="text-[#3b82f6]" />
            <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Control de Accesos</h3>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[
              { l: 'Accesos hoy', v: accessToday, c: '#6b7280' },
              { l: 'En planta ahora', v: inPlant, c: inPlant > 0 ? '#16a34a' : '#6b7280' },
            ].map(s => (
              <div key={s.l} className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold" style={{ fontFamily: 'Barlow, sans-serif', color: s.c }}>{s.v}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{s.l}</p>
              </div>
            ))}
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Últimas entradas</p>
            <div className="space-y-2">
              {access.slice(0, 4).map(a => (
                <div key={a.id} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-xs font-mono font-bold text-gray-900">{a.vehicle_plate}</p>
                    <p className="text-[10px] text-gray-400">{a.driver_name || '—'} · {a.purpose ? a.purpose.slice(0, 25) : '—'}</p>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${a.exit_time ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'}`}>
                    {a.exit_time ? 'Salido' : 'En planta'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Row 4: Open Incidents + Fleet Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Open Incidents */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="incidents-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-[#f59e0b]" />
              <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Incidencias Abiertas / En Curso</h3>
            </div>
            <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{openIncidents.length}</span>
          </div>
          {openIncidents.length === 0 ? (
            <div className="flex items-center justify-center h-24 gap-2 text-green-600">
              <CheckCircle2 size={18} />
              <p className="text-sm font-medium">Sin incidencias abiertas</p>
            </div>
          ) : (
            <div className="space-y-2">
              {openIncidents.slice(0, 5).map(inc => {
                const sev = INC_SEV[inc.severity] || INC_SEV.medium;
                return (
                  <div key={inc.id} className={`border-l-4 rounded-r-lg p-3 ${sev.cls}`} data-testid={`ci-${inc.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-gray-900 truncate">{inc.title}</p>
                        <p className="text-[10px] text-gray-500 mt-0.5">{inc.affected_area && <span>{inc.affected_area} · </span>}{inc.date}</p>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${sev.badge}`}>{sev.l}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Fleet top hours */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="fleet-panel">
          <div className="flex items-center gap-2 mb-4">
            <Truck size={16} className="text-[#16a34a]" />
            <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Top Equipos por Horas Totales</h3>
          </div>
          {kpi?.top_machines?.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">Sin datos de horómetro</p>
          ) : (
            <div className="space-y-2">
              {kpi?.top_machines?.map((m, i) => {
                const maxH = kpi.top_machines[0]?.hours || 1;
                return (
                  <div key={m.name}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="flex items-center gap-2 text-gray-700 truncate">
                        <span className="text-[10px] font-bold text-gray-400 w-4">{i + 1}</span>
                        <span className="truncate max-w-[200px]">{m.name}</span>
                      </span>
                      <span className="font-mono font-bold text-gray-900 flex-shrink-0 ml-2">{m.hours?.toLocaleString('es-ES')} h</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full">
                      <div className="h-full rounded-full transition-all" style={{ width: `${(m.hours / maxH) * 100}%`, backgroundColor: i === 0 ? '#ef4444' : i < 3 ? '#f59e0b' : '#16a34a' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {kpi && (
            <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between text-xs">
              <span className="text-gray-500">Horas totales flota {new Date().getFullYear()}</span>
              <span className="font-bold text-[#16a34a]">{kpi.hours_this_year?.toLocaleString('es-ES')} h</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center pb-2">
        <p className="text-[10px] text-gray-400">Cuadro de Mandos generado el {new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
      </div>
    </div>
  );
}
